// src/components/CDLoan.js

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import '../AdminDashboard.css';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './CDLoan.css';
import { formatDateToDDMMYYYY, getTodayYYYYMMDD } from '../../utils/dateUtils';
import { verifyPassword } from '../../utils/passwordUtils';

const validateField = (name, value) => {
  switch (name) {
    case 'aadhaarDebtor':
    case 'aadhaarGuarantor':
      if (!/^\d{12}$/.test(value)) {
        return 'Aadhaar number must be exactly 12 digits.';
      }
      break;

    case 'phoneDebtor':
    case 'phoneGuarantor':
      if (!/^\d{10}$/.test(value)) {
        return 'Phone number must be exactly 10 digits.';
      }
      break;

    case 'panCard':
      if (!value) {
        return '';
      }
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase())) {
        return 'Invalid PAN format (e.g., ABCDE1234F).';
      }
      break;

    default:
      return '';
  }

  return '';
};

const sanitizeFieldValue = (name, value) => {
  if (name === 'aadhaarDebtor' || name === 'aadhaarGuarantor') {
    return value.replace(/\D/g, '').slice(0, 12);
  }

  if (name === 'phoneDebtor' || name === 'phoneGuarantor') {
    return value.replace(/\D/g, '').slice(0, 10);
  }

  if (name === 'panCard') {
    return value.toUpperCase().slice(0, 10);
  }

  return value;
};

const CDLoan = () => {
  const [formData, setFormData] = useState({ date: getTodayYYYYMMDD() });
  const [debtorPhoto, setDebtorPhoto] = useState(null);
  const [guarantorPhoto, setGuarantorPhoto] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [activeCapture, setActiveCapture] = useState(null);
  const [accountNo, setAccountNo] = useState(1);
  const [formNo, setFormNo] = useState(1);
  const [loanDetails, setLoanDetails] = useState({
    interest: 16,
    dueDate: '', // Single due date for CD loans (maturity date)
  });
  const [showCommission, setShowCommission] = useState(false);
  const [commissionDetails, setCommissionDetails] = useState({
    commissionInterest: 36,
    commissionAmount: 0,
    dc: 100,
    total: 0,
  });
  const [errors, setErrors] = useState({});
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCommissionDetails((prevDetails) => ({
      ...prevDetails,
      commissionInterest: value,
    }));
  };
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const formRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.key === 'c') {
        setShowCommission((prev) => !prev); // Toggle commission section visibility
      }
    };
  
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    const fetchLatestNumbers = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/cd-latest-loan-numbers`);
        const { latestAccountNo, latestFormNo } = response.data;
  
        // Remove CD prefix and increment for next account
        const latestAccountNumber = parseInt(latestAccountNo.slice(2), 10); // Get numeric part
        setAccountNo(latestAccountNumber + 1); // Increment by 1 for the next account number

        // Increment form number by 1
        setFormNo(latestFormNo + 1);
      } catch (error) {
        console.error('Error fetching latest numbers:', error);
        alert('Failed to load the latest account and form numbers.');
      }
    };
  
  fetchLatestNumbers();
  
  // Set today's date in the form data
  const today = getTodayYYYYMMDD();
  setFormData((prev) => ({ ...prev, date: today }));
  }, []);
  
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeFieldValue(name, value);

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, sanitizedValue),
    }));

    const updatedFormData = { ...formData, [name]: sanitizedValue };
    setFormData(updatedFormData);

    if (name === 'loanAmount' || name === 'period' || name === 'date') {
      calculateLoanDetails(updatedFormData);
    }
  };

  const calculateLoanDetails = (data) => {
    const loanAmount = parseFloat(data.loanAmount) || 0;
    const period = parseInt(data.period) || 0;

    // Calculate interest for CD loan
    const interest = ((loanAmount * period * 16) / (365 * 100)).toFixed(2);
    
    // Calculate single due date (maturity date = start date + period)
    const startDate = new Date(data.date);
    const dueDate = new Date(startDate);
    dueDate.setDate(dueDate.getDate() + period);
    const formattedDueDate = dueDate.toISOString().split("T")[0];
 
    setLoanDetails({
      interest,
      dueDate: formattedDueDate,
    });
  };
    

 const calculateCommission = (loanAmount) => {
  const period = parseInt(formData.period) || 0; // Period in days
  const commissionInterest = 36; // Fixed value for commission interest
  const dc = parseFloat(commissionDetails.dc) || 0; // Document Charges

  if (isNaN(loanAmount) || loanAmount <= 0) {
    console.error("Invalid Loan Amount");
    return;
  }

  // Calculate Commission Amount
  const commissionAmount = (
    (loanAmount * commissionInterest * period) / (100 * 360)
  ).toFixed(2);

  // Calculate Total (Commission Amount + DC)
  const total = (parseFloat(commissionAmount) + dc).toFixed(2);

  // Update Commission Details State
  setCommissionDetails((prev) => ({
    ...prev,
    loanAmount,
    commissionInterest,
    commissionAmount,
    total,
  }));
};


  const handleClearForm = () => {
    setFormData({});
    setDebtorPhoto(null);
    setGuarantorPhoto(null);
    setLoanDetails({ interest: 0, dueDate: '' });
  };
  
  const handleOpenCamera = (captureType) => {
    setActiveCapture(captureType);
    setCameraOpen(true);
    navigator.mediaDevices.getUserMedia({ video: true }).then((stream) => {
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
    });
  };
  
  const handleCapture = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 600;

    const context = canvas.getContext('2d');

    // Calculate center crop for square (passport size)
    const videoWidth = videoRef.current.videoWidth;
    const videoHeight = videoRef.current.videoHeight;
    const size = Math.min(videoWidth, videoHeight); // Take smallest dimension to make it square
    const dx = (videoWidth - size) / 2;
    const dy = (videoHeight - size) / 2;

    // Draw the centered square area on canvas
    context.drawImage(videoRef.current, dx, dy, size, size, 0, 0, 600, 600);

    const photo = canvas.toDataURL('image/jpeg');

    if (activeCapture === 'debtor') {
        setDebtorPhoto(photo);
        setFormData((prev) => ({ ...prev, debtorPhoto: photo }));
    } else {
        setGuarantorPhoto(photo);
        setFormData((prev) => ({ ...prev, guarantorPhoto: photo }));
    }

    streamRef.current.getTracks().forEach((track) => track.stop());
    setCameraOpen(false);
};

  const executeSave = async () => {
    // Calculate commission details before saving
    const loanAmount = parseFloat(formData.loanAmount) || 0;
    const period = parseInt(formData.period) || 0;
    const commissionInterest = commissionDetails.commissionInterest || 36;
    
    const commissionAmount = (
      (loanAmount * (commissionInterest / 100) * period) / 360
    ).toFixed(2);
    
    const dc = parseFloat(commissionDetails.dc) || 100;
    const total = (parseFloat(commissionAmount) + dc).toFixed(2);

    setCommissionDetails((prev) => ({
      ...prev,
      commissionAmount,
      total,
    }));

    const loanData =  {
      form_no: formNo,
      account_no: `CD${accountNo}`,
      date: formData.date,
      aadhaar_debtor: formData.aadhaarDebtor,
      pan_card: formData.panCard,
      loanee_name: formData.loaneeName,
      father_name_debtor: formData.fatherNameDebtor,
      address_debtor: formData.addressDebtor,
      phone_debtor: formData.phoneDebtor,
      taluk_debtor: formData.talukDebtor,
      district_debtor: formData.districtDebtor,
      aadhaar_guarantor: formData.aadhaarGuarantor,
      guarantor_name: formData.guarantorName,
      father_name_guarantor: formData.fatherNameGuarantor,
      address_guarantor: formData.addressGuarantor,
      phone_guarantor: formData.phoneGuarantor,
      taluk_guarantor: formData.talukGuarantor,
      district_guarantor: formData.districtGuarantor,
      second_guarantor: formData.secondGuarantor,
      recommended_by: formData.recommended_by,
      loan_amount: loanAmount,
      period: period,
      interest: parseFloat(loanDetails.interest),
      due_date: loanDetails.dueDate, // Single due date for CD loans
      document_charges: 10,
      commission_interest: commissionInterest,
      commission_amount: commissionAmount,
      dc,
      total,
      debtor_photo: debtorPhoto,
      guarantor_photo: guarantorPhoto,
    };
    
    try {
      const response = await axios.post(`${API_BASE_URL}/cd-loan`, loanData);
      alert(`✅ CD Loan has successfully registered!\n\nAccount No: CD${accountNo}\nForm No: ${formNo}\nLoanee: ${formData.loaneeName}\nAmount: ₹${loanAmount.toLocaleString('en-IN')}\nMaturity Date: ${formatDateToDDMMYYYY(loanDetails.dueDate)}`);
      setAccountNo((prev) => prev + 1);
      setFormNo((prev) => prev + 1);
    } catch (error) {
      console.error('Error saving loan data:', error);
      alert('❌ Failed to save CD loan data. Please try again.');
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setPasswordInput('');
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const handlePasswordConfirm = async () => {
    const trimmed = passwordInput.trim();
    if (!trimmed) {
      setPasswordError('Password is required.');
      return;
    }

    let isVerified = false;
    try {
      const isMatch = await verifyPassword(trimmed);
      if (!isMatch) {
        setPasswordError('Incorrect password.');
        return;
      }

      isVerified = true;
      setIsSaving(true);
      setIsPasswordModalOpen(false);
      await executeSave();
    } catch (error) {
      console.error('Password validation failed:', error);
      setPasswordError('Unable to verify password. Please try again.');
    } finally {
      setIsSaving(false);
      if (isVerified) {
        setPasswordInput('');
      }
    }
  };

  const handlePasswordCancel = () => {
    if (isSaving) {
      return;
    }
    setPasswordInput('');
    setPasswordError('');
    setIsPasswordModalOpen(false);
  };
  
  const handlePrint = () => {
    const presentDate = new Date(); // Get the current date
    const periodInDays = parseInt(formData.period); // Convert formData.period to a number
    
    // Calculate dueDate by adding the period (in days) to the current date
    const dueDate = new Date(presentDate);
    dueDate.setDate(dueDate.getDate() + periodInDays);
    
    // Format the dueDate to DD-MM-YYYY format
    const formattedDueDate = formatDateToDDMMYYYY(dueDate);
    if (!formData.loanAmount) {
      alert('Please save the form before printing.');
      return;
    }
    const printContent = `
    <style>
      body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 20px;
      }
      h2 {
        text-align: center;
        margin: 0;
        padding-bottom: 10px;
      }
      p {
        margin: 0;
      }
      .form-container {
        border: 2px solid #000;
        padding: 20px;
        margin: auto;
        width: 95%;
        font-size: 12px;
      }
      .header-section {
        text-align: center;
        margin-bottom: 15px;
      }
      .letter-section {
        margin-bottom: 15px;
        line-height: 1.5;
      }
      .loan-details {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
      }
      .loan-details div {
        width: 45%;
      }
      .loan-details p {
        margin: 0;
        font-weight: bold;
      }
      table {
        width: 100%;
        margin-top: 20px;
        border-collapse: collapse;
      }
      table, th, td {
        border: 1px solid #ddd;
        padding: 8px;
      }
      th {
        background-color: #f2f2f2;
        font-weight: bold;
        text-align: left;
      }
      td {
        font-size: 14px;
        vertical-align: top;
      }
      .signature-section {
        display: flex;
        justify-content: space-between;
        margin-top: 50px;
      }
      .signature {
        text-align: center;
        font-weight: bold;
        width: 30%;
      }
      .photo-section {
        display: flex;
        justify-content: space-between;
        margin-top: 20px;
      }
      .photo-container {
        text-align: center;
        width: 45%;
      }
      .photo-container img {
        width: 100px;
        height: 100px;
        border-radius: 8px;
      }
    </style>
    
    <div class="form-container">
      <div class="header-section">
        <h2>M/s KALABHAIRAV FINANCE AND LEASING (R)
        <p>Khajuri, Aland , Kalaburgi</p>
      </div>
    
      <div class="letter-section">
        <p>To,</p>
        <p><strong>The Managing Partner</strong></p>
        <p>M/s KALABHAIRAV FINANCE AND LEASING (R)
        <p>Khajuri, Aland , Kalaburgi
        <br>
        <p>Sir,</p>
        <p>
          I/We request you to discunt for me/us the bill drawn by me/us for 
          <strong>${formData.period || '___'}</strong> days at 
          <strong>16%</strong> percent of discount.
        </p>
        <p>
          I/We agree to abide by the rules of Time Bill Discount / Cheque Discount 
          as printed below, further agree to execute documents and collateral documents 
          required by you in your favour.
        </p>
        <br>
        <p>Yours faithfully,</p>
      </div>
    
      <div class="loan-details">
        <div>
          <p>Date: ${formData.date ? formatDateToDDMMYYYY(formData.date) : ''}</p>
        </div>
         <div>
          <p>Account no: CD ${accountNo - 1 || ''}</p>
        </div>
        <div style="text-align: right;">
          <p><strong>Rs. ${formData.loanAmount || ''}</strong></p>
        </div>
      </div>
    
      <table>
        <tr>
          <th colspan="2">Debtor Details</th>
        </tr>
        <tr>
          <td>
            Aadhaar No : <strong>${formData.aadhaarDebtor || ''}</strong><br>
            Loanee Name : <strong>${formData.loaneeName || ''}</strong><br>
            Father's Name : <strong> ${formData.fatherNameDebtor || ''}</strong><br>
            Address : <strong>${formData.addressDebtor || ''}</strong><br>
            Phone No : <strong>${formData.phoneDebtor || ''}</strong>
          </td>
          <td class="photo-container">
            ${debtorPhoto ? `<img src="${debtorPhoto}" alt="Debtor Photo">` : '<p>No photo</p>'}
          </td>
        </tr>
      </table>
      <table>
        <tr>
          <th colspan="2">Guarantor Details</th>
        </tr>
        <tr>
          <td>
            Aadhaar No : <strong>${formData.aadhaarGuarantor || ''}</strong><br>
            Guarantor Name : <strong> ${formData.guarantorName || ''}</strong><br>
            Father's Name : <strong>${formData.fatherNameGuarantor || ''}</strong><br>
            Address : <strong>${formData.addressGuarantor || ''}</strong><br>
            Phone No : <strong>${formData.phoneGuarantor || ''}</strong>
          </td>
          <td class="photo-container">
            ${guarantorPhoto ? `<img src="${guarantorPhoto}" alt="Guarantor Photo">` : '<p>No photo</p>'}
          </td>
        </tr>
      </table>
      <table>
      <tr>
          <th colspan="2">Secondary Guarantor Details</th>
        </tr>
        <tr>
        <td>
        Secondary surety : <strong>${formData.secondGuarantor} </strong><p>  
        </p>
        <p> </p> 
        Signature  <br>
        </td>
        </tr>
        </table>
      <table>
        <tr>
          <th>Recommended By</th>
          <th>Loan Amount</th>
          <th>Period (Days)</th>
          <th>Maturity Date</th>
        </tr>
        <tr>
          <td>${formData.recommended_by || ''}</td>
          <td>${formData.loanAmount || ''}</td>
          <td>${formData.period || ''}</td>
          <td>${formattedDueDate || ''}</td>
        </tr>
      </table>
    
      <div class="signature-section">
        <div class="signature">Loanee Signature</div>
        <div class="signature">Guarantor Signature</div>
        <div class="signature">Manager/ Money Lender</div>
      </div>
    </div>
    `;
    
    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write('<html><head><title>Loan Application Print</title></head><body>');
    printWindow.document.write(printContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
     
  };
  const handlePrintCombined = () => {
    const combinedContent = `
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; margin: 0; font-size: 12px; }
        h2 { text-align: center; font-size: 16px; margin-bottom: 10px; }
        .content { margin-top: 10px; line-height: 2; font-size: 12px; line-spacing: 1.5;}
        .signature-section, .signature { margin-top: 20px; font-weight: bold; }
        .signature-section { display: flex; justify-content: space-between; }
        .divider { border-top: 1px solid #000; margin: 15px 0;margin-top: 10px; }
        .footer { margin-top: 30px; text-align: center; font-size: 11px; }
      </style>
  
      <!-- Confirmation Letter -->
      <h2>M/s KALABHAIRAV FINANCE AND LEASING (R)
      <p>Date: ${formData.date ? formatDateToDDMMYYYY(formData.date) : '______'}</p>
      <strong>Account No: CD${accountNo -1 || '______'}</strong>
      <div class="content">
        <p>To,<br> The Managing Partner,<br> M/s KALABHAIRAV FINANCE AND LEASING (R)</p>
        <p>Dear Sir/Madam,</p>
        <p>I/We have borrowed a sum of Rs. <strong>${formData.loanAmount || '______'}</strong> from you in cash. This amount has been discounted through Cheque No. _______ dated <strong>${formData.date ? formatDateToDDMMYYYY(formData.date) : '______'}</strong>, drawn by me/us in favor of M/s KALABHAIRAV FINANCE AND LEASING (R) by me/us in your favor. Please present this cheque for collection on the specified date or thereafter.</p>
        <p>I/We accept full responsibility to repay the borrowed amount to you in the event the cheque is dishonored. Additionally, I/we agree to pay interest at the rate of <strong>16% per annum</strong> from the date of dishonor until the account is fully settled and closed.</p>
        <p>In the unlikely event of loss or damage to this cheque, I/we undertake to issue a replacement cheque or repay the amount in cash, allowing you to collect the payment on the due date without any inconvenience.</p>
      </div>
      <div class="content">
        <p><strong>From Address</strong><br>
        <strong>${formData.loaneeName}</strong><br>
        <strong>${formData.addressDebtor}</strong><br>
        ${formData.talukDebtor}<br>
        ${formData.districtDebtor}</p>
      </div>
      <div class="signature-section">
        <div>Sincerely,</div>
        <div>${formData.loaneeName || ''}</div>
      </div>
  
      <div class="divider"></div>
  
      <!-- Declaration Letter -->
      <h2>DECLARATION</h2>
      <p>Date: ${formData.date ? formatDateToDDMMYYYY(formData.date) : '______'}</p>
      <strong>Account No: CD${accountNo - 1|| '______'}</strong>
      <div class="content">
        <p>I/We hereby apply for a loan with M/s KALABHAIRAV FINANCE AND LEASING (R)
        <p>${formData.addressDebtor}<br>
        ${formData.talukDebtor}<br>
        ${formData.districtDebtor}</p>
        <p>I/We, <strong>${formData.loaneeName || ''}</strong>, residing at ${formData.addressDebtor || '________'}, TQ: ${formData.talukDebtor || '________'},</p>
        <p>do hereby declare that my/our annual income is not less than Rs. 15,000/- (Rupees Fifteen Thousand only) and that I/we do not belong to the economically weaker section as defined under the Karnataka Debt Relief Act, 1980.</p>
        <p>I/We affirm that the contents of this declaration are true and accurate to the best of my/our knowledge and belief.</p>
      </div>
      <div class="signature">
        <p>Signature/s</p>
        <p>1. __________________</p>
      </div>
  
    
    `;
  
    // Open a new window and print
    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write('<html><head><title>Combined Document</title></head><body>');
    printWindow.document.write(combinedContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };



  const handlePrintPronoteAndCashReceipt = () => {
    const combinedContent = `
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; margin: 0; font-size: 12px; }
        h2 { text-align: center; font-size: 16px; margin-bottom: 10px; }
        .content { margin-top: 10px; line-height: 2; font-size: 12px; }
        .divider { border-top: 1px solid #000; margin: 25px 0; }
        .footer { margin-top: 80px; text-align: center; font-size: 11px; }
  
        /* Signature styling */
        .signature-section {
          margin-top: 40px;
          display: flex;
          justify-content: flex-end;
        }
        .signature-box {
          width: 200px;
          height: 60px;
          border: 1px solid #000;
          text-align: center;
          padding-top: 40px;
          font-weight: bold;
          font-size: 12px;
        }
  
        /* For layout spacing */
        .witnesses { margin-top: 15px; }
        .witnesses p { margin: 2px 0; }
      </style>
  
      <!-- Pronote -->
      <h2>PROMISSORY NOTE</h2>
      <p>Location: Khajuri, Aland , Kalaburgi</p>
      <p>Date: <strong>${formData.date ? formatDateToDDMMYYYY(formData.date) : "__________"}</strong></p>
      <p>Account no: <strong>CD ${accountNo - 1 || "__________"}</strong></p>
  
      <div class="content">
        <p>Amount: Rs. <strong>${formData.loanAmount || "__________"}</strong></p>
        <p>On Demand, I/We <strong>${formData.loaneeName || "__________"}</strong>, S/o ${formData.fatherNameDebtor || "__________"}</p>
        <p>Address: <strong>${formData.addressDebtor || "__________"}</strong>, TQ: <strong>${formData.talukDebtor || "__________"}</strong></p>
        <p>Hereby promise to pay the Managing Partner, 
          <strong>M/s KALABHAIRAV FINANCE AND LEASING (R)</strong>, 
          the sum of Rupees <strong>${formData.loanAmount || "__________"}</strong> only, together with interest at the rate of 
          <strong>16%</strong> per annum, effective from the date of payment: 
          <strong>${formData.date ? formatDateToDDMMYYYY(formData.date) : "__________"}</strong>.
        </p>
      </div>
  
      <div class="witnesses">
        <p>Witnesses:</p>
        <p>-</p>
        <p>1. ${formData.guarantorName || "_________________"}</p>
      </div>
  
      <div class="signature-section">
        <div class="signature-box">Signature</div>
        <p>${formData.loaneeName || "_________________"}</p>
      </div>
  
      <div class="divider"></div>
  
      <!-- Cash Receipt -->
      <h2>CASH RECEIPT</h2>
      <p>Location: Khajuri, Aland , Kalaburgi</p>
      <p>Date: <strong>${formData.date ? formatDateToDDMMYYYY(formData.date) : "__________"}</strong></p>
      <p>Account no: <strong>CD ${accountNo - 1 || "__________"}</strong></p>
  
      <div class="content">
        <p>Received a sum of Rupees <strong>Rs. ${formData.loanAmount || "__________"}</strong> 
        (in words: <strong>${formData.loanAmount ? convertToWords(formData.loanAmount) : "_________________"} RUPEES</strong>) 
        from <strong>M/s KALABHAIRAV FINANCE AND LEASING (R)</strong>, executed in favor of the recipient on the date 
        <strong>${formData.date ? formatDateToDDMMYYYY(formData.date) : "__________"}</strong>, 
        for the purpose of a loan agreement amounting to a total of Rs. 
        <strong>${formData.loanAmount || "__________"}</strong> 
        (in words: <strong>${formData.loanAmount ? convertToWords(formData.loanAmount) : "_________________"} RUPEES</strong>).</p>
        <p>This receipt is issued in connection with my/our loan agreement.</p>
      </div>
  
      <div class="witnesses">
        <p>Witnesses:</p>
        <p>-</p>
        <p>1. ${formData.guarantorName || "_________________"}</p>
      </div>
  
      <div class="signature-section">
        <div class="signature-box">Signature</div>
        <p>${formData.loaneeName || "_________________"}</p>
      </div>
  
      
    `;
  
    // Open a new window and print
    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write('<html><head><title>Combined Document</title></head><body>');
    printWindow.document.write(combinedContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };
  
  const handleBillPrint = () => {
    const presentDate = new Date(); // Get the current date
    const periodInDays = parseInt(formData.period); // Convert formData.period to a number

    // Calculate dueDate by adding the period (in days) to the current date
    const dueDate = new Date(presentDate);
    dueDate.setDate(dueDate.getDate() + periodInDays);

    // Format the dueDate to DD-MM-YYYY format
    const formattedDueDate = formatDateToDDMMYYYY(dueDate);
    const billContent = `
<style>
  body { font-family: Arial, sans-serif; padding: 20px; margin: 0; font-size: 12px; }
  .page { display: flex; justify-content: space-between; flex-direction: row; }
  .bill { width: 48%; border: 1px solid #000; padding: 20px; box-sizing: border-box; position: relative; }
  .bill-header { font-size: 1.5em; font-weight: bold; text-align: center; margin-top: 20px; }
  .bill-sub-header { text-align: center; margin-top: 5px; font-size: 1em; }
  .content { margin-top: 20px; line-height: 1; }
  .table { width: 100%; margin-top: 20px; border-collapse: collapse; }
  .table th, .table td { border: 1px solid #000; padding: 8px; text-align: left; font-size: 12px; }
  .table th { background-color: #f2f2f2; }
  .footer { display: flex; justify-content: space-between; margin-top: 50px; }
  .footer div { text-align: center; width: 30%; font-weight: bold; }
  .box-container {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 20px;
  }
  .box {
    border: 1px solid #000;
    padding: 5px 10px;
    margin-right: 10px;
    text-align: left;
    font-size: 12px;
    background-color: #fff;
  }
  .box span {
    display: block;
    font-size: 14px;
  }
  .box small {
    display: block;
    font-size: 10px;
  }
  .gl-box {
    border: 1px solid #000;
    padding: 10px;
    font-size: 20px;
    font-weight: bold;
    background-color: #fff;
    margin-right: 10px
  }
  .divider {
    border-top: 1px solid #000;
    margin-top: 10px;
    margin-bottom: 10px;
  }
</style>

<div class="page">
  <!-- First Bill -->
  <div class="bill">
    <div class="bill-header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="bill-sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="divider"></div>
    <div class="box-container">
      <div class="gl-box">CD</div>
      <div class="box">
        <span>No - <strong>${formNo - 1} </strong></span>
        <span>Date - ${formData.date ? formatDateToDDMMYYYY(formData.date) : '__________'}</span>
      </div>
      <div class="box">
        <span><strong>FORM NO - 6</strong></span>
        <small>(See Rule 15)</small>
      </div>
    </div>
    <div class="divider"></div>
    <div class="content">
      <div><strong>Statement Showing the Details of the Loan</strong></div>
    </div>
    <table class="table">
      <tr><th>Name of Loanee</th><td>${formData.loaneeName}</td></tr>
      <tr><th>Address</th><td>${formData.addressDebtor}</td></tr>
      <tr><th>Amount of Loan</th><td>₹ ${formData.loanAmount}</td></tr>
      <tr><th>Date of Loan</th><td>${formData.date ? formatDateToDDMMYYYY(formData.date) : '__________'}</td></tr>
      <tr><th>Loan Maturity (if any)</th><td>${formattedDueDate}</td></tr>
      <tr><th>Rate of Interest (per annum)</th><td>16%</td></tr>
      <tr><th>Name of Guarantor</th><td>${formData.guarantorName}</td></tr>
      <tr><th>Guarantor Address</th><td>${formData.addressGuarantor}</td></tr>
      <tr><th>Document Details</th><td>General Loan</td></tr>
      <tr><th>Special Conditions (if any)</th><td>Cheque No.</td></tr>
      <tr><th>Name & Address of Money Lender</th><td>M/s KALABHAIRAV FINANCE AND LEASING (R), Khajuri, Aland , Kalaburgi
    </table>
    <div class="footer">
      <div>Signature of Loanee</div>
      <div>Signature of Guarantor</div>
      <div>Signature of Money Lender</div>
    </div>
  </div>

  <!-- Second Bill (Duplicate Content) -->
  <div class="bill">
    <div class="bill-header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
    <div class="bill-sub-header">Khajuri, Aland , Kalaburgi</div>
    <div class="divider"></div>
    <div class="box-container">
      <div class="gl-box">CD</div>
      <div class="box">
        <span>No - <strong>${formNo -1 }  </strong></span>
        <span>Date - ${formData.date ? formatDateToDDMMYYYY(formData.date) : '__________'}</span>
      </div>
      <div class="box">
        <span><strong>FORM NO - 6</strong></span>
        <small>(See Rule 15)</small>
      </div>
    </div>
    <div class="divider"></div>
    <div class="content">
      <div><strong>Statement Showing the Details of the Loan</strong></div>
    </div>
    <table class="table">
      <tr><th>Name of Loanee</th><td>${formData.loaneeName}</td></tr>
      <tr><th>Address</th><td>${formData.addressDebtor}</td></tr>
      <tr><th>Amount of Loan</th><td>₹ ${formData.loanAmount}</td></tr>
      <tr><th>Date of Loan</th><td>${formData.date ? formatDateToDDMMYYYY(formData.date) : '__________'}</td></tr>
      <tr><th>Loan Maturity (if any)</th><td>${formattedDueDate}</td></tr>
      <tr><th>Rate of Interest (per annum)</th><td>16%</td></tr>
      <tr><th>Name of Guarantor</th><td>${formData.guarantorName}</td></tr>
      <tr><th>Guarantor Address</th><td>${formData.addressGuarantor}</td></tr>
      <tr><th>Document Details</th><td>CD Loan</td></tr>
      <tr><th>Special Conditions (if any)</th><td>Cheque No.</td></tr>
      <tr><th>Name & Address of Money Lender</th><td>M/s KALABHAIRAV FINANCE AND LEASING (R), Khajuri, Aland , Kalaburgi</td></tr>
    </table>
    <div class="footer">
      <div>Signature of Loanee</div>
      <div>Signature of Guarantor</div>
      <div>Signature of Money Lender</div>
    </div>
  </div>
</div>
`;

    const billWindow = window.open('', '', 'height=800,width=1200');
    billWindow.document.write('<html><head><title>Bill Print</title></head><body>');
    billWindow.document.write(billContent);
    billWindow.document.write('</body></html>');
    billWindow.document.close();
    billWindow.print();
};



  const handlePrintRules = () => {
    const rulesContent = `
       <style>
      body {
        font-family: Arial, sans-serif;
        margin: 20px;
        line-height: 1.5;
        color: #000;
      }
      h2, h3 {
        text-align: center;
        margin: 3px;
      }
      .header-box {
        border: 2px solid #000;
        padding: 10px;
        margin-bottom: 20px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
      }
      .header-box p {
        margin: 5px;
      }
      .header-title {
        font-size: 18px;
        font-weight: bold;
        text-align: center;
      }
      .rules-title {
        text-align: center;
        text-decoration: underline;
        margin-bottom: 20px;
      }
      ul {
        margin: 20px 0;
        padding-left: 20px;
      }
      li {
        margin-bottom: 6px;
        list-style-type: disc;
      }
      .signature-section {
        margin-top: 30px;
      }
      .signature {
        display: flex;
        justify-content: space-between;
        margin-top: 10px;
        font-weight: bold;
      }
      .recommendation {
        margin-top: 30px;
        display: flex;
        justify-content: space-between;
        font-weight: bold;
      }
    </style>

    <h2>M/s KALABHAIRAV FINANCE AND LEASING (R)</h2>
    <p class="header-title">Khajuri, Aland , Kalaburgi</p>

    <!-- Header Box -->
    <div class="header-box">
      <p><strong>Aadhaar No:</strong> ${formData.aadhaarGuarantor || ''}</p>
      <p><strong>Advanced on Date:</strong> ${formData.date ? formatDateToDDMMYYYY(formData.date) : ''}</p>
      <p><strong>Name (Loanee):</strong> ${formData.loaneeName || ''}</p>
      <p><strong>Account No:</strong> CD ${accountNo - 1}</p>
      <p><strong>Sanctioned Rs.:</strong> ₹ ${formData.loanAmount || ''}</p>
      <p><strong>Father's Name:</strong> ${formData.fatherNameDebtor || ''}</p>
    </div>

      <h3 style="text-align:center; text-decoration:underline; margin-bottom:20px;">RULES</h3>
      <ul>
        <li>The Firm serves all traders without discrimination.</li>
        <li>The Firm will advance loans to the Traders and Depositors depending upon their limits and credibility.</li>
        <li>The traders requiring financial assistance shall apply in the prescribed format obtained from the office and shall receive the advance executing necessary documents in favor of the Firm. Loanee shall be bound by the Rules of the Firm and terms and conditions of the loan advanced.</li>
        <li>The discretion to sanction or reject the application for loan of trade shall vest with the Firm and the authority of the Firm in this regard cannot be questioned.</li>
        <li>The intending loanees shall furnish all information called by the Firm in respect of their assets, liabilities and other properties to enable the Firm to consider their application for loan. Failure to furnish such information would be a sufficient reason for rejection of the application.</li>
        <li>The advances against negotiable instruments shall be mainly on the basis of customer's undertaking to be liable for the money received by them.</li>
        <li>The loanee who fails to repay the loan amount on due date in full shall be entitled for the belted interest at 16% per annum due from the date of default, till the loan amount is repaid in full.</li>
        <li>The Finance shall be entitled to collect the amount of credit of the loanee or his surety in any account with the Finance against outstanding dues under the loan account with or without the consult of the said loanee and surety.</li>
        <li>The facility provided by the Finance for the convenient repayment of loan or advance by opening and discounting in hundi daily account shall be availed voluntarily by the loanee.</li>
        <li>The loanee shall be entitled to receive the sanctioned loan or advance offering surety of another trader to the satisfaction of the Finance.</li>
        <li>The loanee and his surety shall be jointly and severally liable for repayment of the money advanced.</li>
        <li>The loanee shall be at liberty to obtain classification in respect of his account and to verify the same to satisfy himself / herself as to their correctness.</li>
        <li>The loanee shall bear all necessary expenses for the execution of valid documents in respect of any loan or advance in favor of the Finance.</li>
        <li>Besides the Finance shall collect service charges from its customer.</li>
        <li>The Traders who have availed the facility of loan under TBD/MGL/CD account shall not be eligible for loan on their deposit in other account.</li>
        <li>The cost of summons must be paid by the parties.</li>
        <li>Bills sanctioned and not availed within 3 days of such advice will be treated as cancelled. Thereafter if the party requires the amount, fresh sanction should be obtained.</li>
        <li>When bills signed by two or more parties, their liabilities will be considered as joint and several.</li>
        <li>All bills are payable at the Finance office.</li>
        <li>The date of payment must be ascertained by constituent. If the due date falls on weekly holiday or any other holiday, payment should be made on the previous working day. The Finance is not bound to give notice of due dates but may do so as a matter of courtesy. No days of grace are allowed.</li>
        <li>Failure to repay a bill on the due date may result in the cancellation of the constituent's credit limit.</li>
        <li>Overdue interest will be charged and collected @ 16% p.a. on quarterly rest.</li>
        <li>The Finance has the first charge on credit balance in all the account of the signatories and it can adjust such amounts against the dues of the signatories as per rules.</li>
        <li>In case of disputes, suits, etc., the courts as in which the Finance operates shall have the jurisdiction.</li>
        <li>The applicants shall be trend by the Rules of the Finance.</li>
        <li>The belated interest will be calculated on the balance amount remaining due on the due date and shall be recoverable. Any amount paid after the due date shall not be adjusted against loan balance remaining unless given in full settlement of outstanding dues.</li>
        <li>The above rules and all the other rules were, gone through by me/us read over and explained to me/us and having understood fully, I/we hereby bind overself and declare that I/We shall abide by the said rules.</li>
      </ul>
  
      <div class="signature-section">
        <p><strong>Signature of Applicant/s</strong></p>
        <div class="signature">
          <span>1. ....................................................... </span>
          <span>2. ....................................................... </span>
        </div>
      </div>
  
      <div class="recommendation">
        <span>Manager / Accountant</span>
        <span>Managing Partner</span>
      </div>
    `;
  
    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write('<html><head><title>Rules and Terms</title></head><body>');
    printWindow.document.write(rulesContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };
  const handleDebitVoucherPrint = (loanData) => {
    // Extract and format required values dynamically
    const loanAmount = parseFloat(formData.loanAmount || 0).toFixed(2);
  
    const slipContent = `
  <style>
    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
    .container { display: flex; }
    .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
    .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
    .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
    .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
    .details { margin: 10px 0; }
    .line { display: flex; justify-content: space-between; margin: 5px 0; }
    .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
    .table td { padding: 5px; }
    .table .right { text-align: right; font-weight: bold; }
    .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
    .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
  </style>
  
  <div class="container">
    <!-- Left Receipt -->
    <div class="left-receipt">
      <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
      <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
      <div class="details">
        <div class="line"><strong>A/C No:</strong> CD ${accountNo - 1}</div>
        <div class="line"><strong>Date:</strong> ${formData.date ? formatDateToDDMMYYYY(formData.date) : '__________'}</div>
      </div>
      <div class="details">
        <div class="line"><strong>Loan Amount Rs:</strong> ₹ ${loanAmount}</div>
        <div class="line"><strong>Given to :</strong> ${formData.loaneeName}</div>
      </div>
      <table class="table">
        <tr>
          <td>Loan Amount A/c</td>
          <td class="right">₹ ${loanAmount}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹ ${loanAmount}
      </div>
      <div class="footer">
        Cashier / Mg. Partner
      </div>
    </div>
  
    <!-- Right Receipt -->
    <div class="right-receipt">
      <div class="header">M/s KALABHAIRAV FINANCE AND LEASING (R)</div>
      <div class="sub-header">Khajuri, Aland , Kalaburgi</div>
      <div class="details">
        <div class="line"><strong>A/C No:</strong> CD ${accountNo - 1}</div>
        <div class="line"><strong>Date:</strong> ${formData.date ? formatDateToDDMMYYYY(formData.date) : '__________'}</div>
      </div>
      <div class="details">
        <div class="line"><strong>Loan Amount Rs:</strong> ₹ ${loanAmount}</div>
        <div class="line"><strong>Given to:</strong> ${formData.loaneeName}</div>
      </div>
      <table class="table">
        <tr>
          <td>Loan Amount A/c</td>
          <td class="right">₹ ${loanAmount}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹ ${loanAmount}
      </div>
      <div class="footer">
        Cashier / Mg. Partner
      </div>
    </div>
  </div>
  `;
  
    const printWindow = window.open('', '', 'height=800,width=1200');
    printWindow.document.write('<html><head><title>Loan Slip</title></head><body>');
    printWindow.document.write(slipContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };
function convertToWords(number) {
  const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const thousands = ["", "Thousand", "Million", "Billion"];

  if (number === 0) return "Zero";

  let word = "";
  let thousandIndex = 0;

  while (number > 0) {
    if (number % 1000 !== 0) {
      word = convertHundred(number % 1000) + thousands[thousandIndex] + " " + word;
    }
    number = Math.floor(number / 1000);
    thousandIndex++;
  }

  return word.trim();

  function convertHundred(num) {
    let result = "";

    if (num > 99) {
      result += units[Math.floor(num / 100)] + " Hundred ";
      num %= 100;
    }

    if (num > 10 && num < 20) {
      result += teens[num - 10] + " ";
    } else {
      result += tens[Math.floor(num / 10)] + " ";
      result += units[num % 10] + " ";
    } return result;
  }
}
return (
  <div className="cd-loan-container">
    

    <form className="cd-loan-form" ref={formRef} onSubmit={handleFormSubmit}>
      <h2>CD New Loan Form</h2>

      <div className="input-grid">
        <div>
          <label>Account No:</label>
          <input type="text" value={`CD${accountNo}`} readOnly />
        </div>
        <div>
          <label>Form No 6:</label>
          <input type="text" value={formNo}  readOnly />
        </div>

        <div>
          <label>Date:</label>
          <input 
            type="date" 
            name="date" 
            value={formData.date} // Bind state value to input
            onChange={handleChange} 
            required 
          />
        </div>
      </div>
      
      <h3>Loanee Details</h3>
<div className="input-grid">
  <div>
    <label htmlFor="aadhaarDebtor">AADHAAR No </label>
    <input
      type="text"
      id="aadhaarDebtor"
      name="aadhaarDebtor"
      value={formData.aadhaarDebtor || ''}
      required
      onChange={handleChange}
      maxLength={12}
      inputMode="numeric"
    />
    {errors.aadhaarDebtor && <p className="error">{errors.aadhaarDebtor}</p>}
  </div>
  <div>
    <label htmlFor="panCard">PAN Card </label>
    <input
      type="text"
      id="panCard"
      name="panCard"
      value={formData.panCard || ''}
      onChange={handleChange}
      maxLength={10}
      style={{ textTransform: 'uppercase' }}
    />
    {errors.panCard && <p className="error">{errors.panCard}</p>}
  </div>
  <div>
    <label htmlFor="loaneeName">Loanee Name </label>
    <input type="text" id="loaneeName" name="loaneeName" required onChange={handleChange} />
  </div>
  <div>
    <label htmlFor="fatherNameDebtor">Father's Name </label>
    <input type="text" id="fatherNameDebtor" name="fatherNameDebtor" required onChange={handleChange} />
  </div>
  <div>
    <label htmlFor="phoneDebtor">Phone No </label>
    <input
      type="tel"
      id="phoneDebtor"
      name="phoneDebtor"
      value={formData.phoneDebtor || ''}
      required
      onChange={handleChange}
      maxLength={10}
      inputMode="numeric"
    />
    {errors.phoneDebtor && <p className="error">{errors.phoneDebtor}</p>}
  </div>
  <div>
    <label htmlFor="addressDebtor">Address </label>
    <textarea id="addressDebtor" name="addressDebtor" rows="2" required onChange={handleChange}></textarea>
  </div>
  <div>
    <label htmlFor="talukDebtor">Taluk </label>
    <input type="text" id="talukDebtor" name="talukDebtor" required onChange={handleChange} />
  </div>
  <div>
    <label htmlFor="districtDebtor">District </label>
    <input type="text" id="districtDebtor" name="districtDebtor" required onChange={handleChange} />
  </div>
  <div className="photo-container">
    <label>Photo </label>
    <button type="button" className='Capt' onClick={() => handleOpenCamera('debtor')}>Capture Photo</button>
    {debtorPhoto && (
  <img
    src={debtorPhoto}
    alt="Debtor"
    className="photo-preview"
    style={{ width: "150px", height: "200px" }} // Set desired width and height
  />
)}</div>
</div>
<h3>Guarantor Details </h3>
<div className="input-grid">
  <div>
    <label htmlFor="aadhaarGuarantor">AADHAAR No</label>
    <input
      type="text"
      id="aadhaarGuarantor"
      name="aadhaarGuarantor"
      value={formData.aadhaarGuarantor || ''}
      required
      onChange={handleChange}
      maxLength={12}
      inputMode="numeric"
    />
    {errors.aadhaarGuarantor && <p className="error">{errors.aadhaarGuarantor}</p>}
  </div>
  <div>
    <label htmlFor="guarantorName">Guarantor Name</label>
    <input type="text" id="gurantorName" name="guarantorName" required onChange={handleChange} />
  </div>
  <div>
    <label htmlFor="fatherNameGuarantor">Father's Name</label>
    <input type="text" id="fatherNameGuarantor" name="fatherNameGuarantor" required onChange={handleChange} />
  </div>
  <div>
    <label htmlFor="phoneGuarantor">Phone No</label>
    <input
      type="tel"
      id="phoneGuarantor"
      name="phoneGuarantor"
      value={formData.phoneGuarantor || ''}
      required
      onChange={handleChange}
      maxLength={10}
      inputMode="numeric"
    />
    {errors.phoneGuarantor && <p className="error">{errors.phoneGuarantor}</p>}
  </div>
  <div>
    <label htmlFor="addressGuarantor">Address</label>
    <textarea id="addressGuarantor" name="addressGuarantor" rows="2" required onChange={handleChange}></textarea>
  </div>
 
  <div>
    <label htmlFor="talukGuarantor">Taluk</label>
    <input type="text" id="talukGuarantor" name="talukGuarantor" required onChange={handleChange} />
  </div>
  <div>
    <label htmlFor="districtGuarantor">District</label>
    <input type="text" id="districtGuarantor" name="districtGuarantor" required onChange={handleChange} />
  </div>
  <div>
    <label htmlFor="secondGuarantor">Second Guarantor</label>
    <input type="text" id="secondGuarantor" name="secondGuarantor" onChange={handleChange} />
  </div>
  <div className="photo-container">
    <label>Photo</label>
    <button type="button" className='Capt' onClick={() => handleOpenCamera('guarantor')}>Capture Photo</button>
    {guarantorPhoto && (
  <img
    src={guarantorPhoto}
    alt="Guarantor"
    className="photo-preview"
    style={{ width: "150px", height: "200px" }} // Adjust the size as needed
  />
)}
</div>
</div>
<h3>Loan Information</h3>
<div className="input-grid">
  <div>
    <label>Recommended By:</label>
    <select
      id="recommended_by"
      name="recommended_by"
      value={formData.recommended_by || ''}
      onChange={handleChange}
      required
    >
      <option value="">Select Partner</option>
      <option value="Sri Rajkumar">Sri Rajkumar</option>
      <option value="Sri Akash">Sri Akash</option>
      <option value="Smt Siddamma">Smt Siddamma</option>
      <option value="Sri Mahesh">Sri Mahesh</option>
      <option value="Sri Vishwanth">Sri Vishwanth</option>
      <option value="Sri Vishal">Sri Vishal</option>
      <option value="Sri Nikhil">Sri Nikhil</option>
      <option value="Sri Shankar">Sri Shankar</option>
      <option value="Sri Khandu Ghante">Sri Khandu Ghante</option>
      <option value="Sri Sharanasabappa">Sri Sharanasabappa</option>
    </select>
  </div>
  <div>
  <label>Loan Amount:</label>
  <input
    type="number"
    name="loanAmount"
    placeholder="Enter Loan Amount"
    value={formData.loanAmount || ''}
    onChange={(e) => {
      handleChange(e);
      const updatedLoanAmount = e.target.value;
      calculateCommission(updatedLoanAmount);
    }}
  />
</div>
  <div>
    <label>Period (Days):</label>
    <input type="number" name="period" required placeholder="Period (Days)" onChange={handleChange} />
  </div>

  <div>
    <label>Interest:</label>
    <input type="text" value={loanDetails.interest} readOnly placeholder="Interest" />
  </div>

  <div>
    <label>Maturity Date (Due Date):</label>
    <input type="text" value={loanDetails.dueDate ? formatDateToDDMMYYYY(loanDetails.dueDate) : ''} readOnly placeholder="Maturity Date" />
  </div>


  <div>
    <label>Document Charges:</label>
    <input
      type="number"
      value= '10'
      readOnly
    />
  </div>
</div>
{showCommission && (
  <div className="input-grid">
    <h3>Commission Details</h3>

    <div>
  <label>Commission Interest (%):</label>
  <input
    type="number"
    value={commissionDetails.commissionInterest}
    onChange={handleInputChange}
  />

</div>

<div>
  <label>Commission Amount:</label>
  <input
    type="text"
    value={commissionDetails.commissionAmount}
    readOnly
  />
</div>
    <div>
      <label>DC:</label>
      <input
        type="number"
        name="dc"
        placeholder="Document Charges"
        value={100}
      />
    </div>

    <div>
      <label>Total:</label>
      <input
        type="text"
        value={commissionDetails.total}
        readOnly
        placeholder="Total"
      />
    </div>
  </div>
)}

        <div className="form-actions">
          <button type="submit" className='Capt' disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
          <button type="button" className='Capt' onClick={handlePrint}>Print</button>
          <button type="button" className="Capt" onClick={handlePrintRules}>Rules</button>
          <button type="button"className='Capt'  onClick={handlePrintCombined}>Confirm Letter & Declaration</button>
          
          <button type="button" className='Capt' onClick={handlePrintPronoteAndCashReceipt}>Pronote and Cash Receipt</button>
          <button type="button" className='Capt' onClick={handleBillPrint}>Bill</button>
          <button type="button" className='Capt' onClick={handleDebitVoucherPrint}>Debit Voucher Print</button> 
          <button type="button" className='Capt'  onClick={handleClearForm}>Clear All</button>
          <button type="button" className='Capt' onClick={() => navigate(-1)}>BACK</button>
        </div>
      </form>
      {cameraOpen && (
        <div className="camera-modal">
         <div className="video-frame">
    <video ref={videoRef} autoPlay />
</div>
      <button className="Capt" onClick={handleCapture}>Capture Photo</button>
        </div>
      )}
      {isPasswordModalOpen && (
        <div className="password-modal-overlay">
          <div className="password-modal">
            <h3>Password required</h3>
            <input
              type="password"
              value={passwordInput}
              onChange={(event) => setPasswordInput(event.target.value)}
              placeholder="Enter password"
              autoFocus
              disabled={isSaving}
            />
            {passwordError && <p className="error">{passwordError}</p>}
            <div className="modal-actions">
              <button
                type="button"
                className="Capt"
                onClick={handlePasswordConfirm}
                disabled={isSaving}
              >
                {isSaving ? 'Verifying...' : 'Confirm'}
              </button>
              <button
                type="button"
                className="Capt"
                onClick={handlePasswordCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default CDLoan;

  
